import React, { useState, ReactNode } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Popover from '../../atoms/Popover/Popover';

interface InfoPopoverComponentProps {
  children: ReactNode;
  placement?:
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';
  width?: number | string;
  iconProps?: {
    color?: string;
    fontSize?: 'small' | 'medium' | 'large' | 'inherit';
    style?: React.CSSProperties;
  };
  contentStyle?: React.CSSProperties;
}

/**
 * InfoPopoverComponent - A reusable info icon with popover that supports children props
 *
 * @param {ReactNode} children - Content to display inside the popover
 * @param {string} placement - Placement of the popover
 * @param {number|string} width - Width of the popover
 * @param {object} iconProps - Props for customizing the InfoIcon
 * @param {object} contentStyle - Additional styles for the popover content
 */
const InfoPopoverComponent: React.FC<InfoPopoverComponentProps> = ({
  children,
  placement = 'bottom',
  width = 300,
  iconProps = {},
  contentStyle = {},
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    setAnchorEl(event.currentTarget as unknown as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <span className="inline-flex align-middle">
      <InformationCircleIcon
        onClick={handleClick}
        className="h-4 w-4 cursor-pointer text-gray-400"
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        placement={placement}
        width={width}
        contentStyle={{ ...contentStyle }}
      >
        {children}
      </Popover>
    </span>
  );
};

export default InfoPopoverComponent;
